import {
  Box,
  Table,
  Tr,
  Thead,
  Tbody,
  Th,
  Td,
  Image,
  NumberInput,
  NumberInputField,
  Text,
  HStack,
  Checkbox,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  VStack,
  Center,
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  updatePaymentOptions,
  updateGeneralPayment,
  updateCommission,
  updateLogoUrl,
} from '../../features/labor/paymentSlice';
import { updateHasBeenSaved } from '../../features/labor/laborSlice';

import axios from 'axios';
import useUser from '../../hooks/useUser';
import mixpanel from 'mixpanel-browser';
import { QuestionIcon, AddIcon, DeleteIcon } from '@chakra-ui/icons';
import Overhead from './../Overhead';

const formatFileSize = bytes =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round((bytes || 0) / 1024))} KB`;

const PaymentOptions = ({
  checkPaymentCompletion,
  isPaymentGeneralComplete,
  isPaymentOptionsComplete,
  isPaymentCommissionComplete,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const inputRef = useRef(null);
  const payment = useSelector(state => state.payment);
  const logoUrl = useSelector(state => state.payment.logoUrl);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const { user } = useUser();
  const userEmail = user ? user.email : null;

  const savedList = useSelector(state => state.labor.hasBeenSaved);
  const [currentTab, setCurrentTab] = useState(0);
  const [openModal, setOpenModal] = useState(false);

  const [logoPreview, setLogoPreview] = useState(logoUrl || null);
  const [isLogoUploading, setIsLogoUploading] = useState(false);

  // The saved logo URL can arrive after this tab mounts.
  useEffect(() => {
    if (logoUrl) setLogoPreview(logoUrl);
  }, [logoUrl]);

  const libraryInputRef = useRef(null);
  const [libraryItems, setLibraryItems] = useState([]);
  const [libraryUploading, setLibraryUploading] = useState(false);
  const [libraryBusyId, setLibraryBusyId] = useState(null);

  const fetchLibrary = async () => {
    try {
      const token = await user.getIdToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/pdf/library`,
        {
          headers: { authorization: `${token}` },
          params: { username: user.email },
        }
      );
      setLibraryItems(response.data || []);
    } catch (error) {
      console.error('Could not load attachment library:', error);
    }
  };

  useEffect(() => {
    if (user) fetchLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const libraryToast = (title, description, status = 'error') =>
    toast({ title, description, status, duration: 4000, isClosable: true });

  const handleLibraryUpload = async e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') {
      libraryToast('Please choose a PDF');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      libraryToast('File is too large', 'Library PDFs must be under 25 MB.');
      return;
    }

    setLibraryUploading(true);
    try {
      const token = await user.getIdToken();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('username', user.email);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/pdf/library`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            authorization: `${token}`,
          },
        }
      );
      setLibraryItems(items => [response.data, ...items]);
    } catch (error) {
      console.error('Library upload failed:', error);
      libraryToast(
        'Upload failed',
        error.response?.data?.message || 'Please try again.'
      );
    }
    setLibraryUploading(false);
  };

  const handleLibraryDelete = async item => {
    if (
      !window.confirm(
        `Delete "${item.name}" from the library? Estimates that already use it keep their copy.`
      )
    ) {
      return;
    }
    setLibraryBusyId(item.id);
    try {
      const token = await user.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/pdf/library/delete`,
        { username: user.email, id: item.id },
        { headers: { authorization: `${token}` } }
      );
      setLibraryItems(items => items.filter(i => i.id !== item.id));
    } catch (error) {
      console.error('Library delete failed:', error);
      libraryToast('Could not delete', 'Please try again.');
    }
    setLibraryBusyId(null);
  };

  const MAX_WIDTH = 600;
  const MAX_HEIGHT = 600;
  const MAX_FILE_SIZE = 1024 * 1024;

  const readImageSize = file =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new window.Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(url);
      };
      img.onerror = () => {
        reject(new Error('Unable to read image.'));
        URL.revokeObjectURL(url);
      };
      img.src = url;
    });

  const logoError = description =>
    toast({
      title: 'Logo not uploaded',
      description,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });

  const handleLogoUpload = async e => {
    const file = e.target.files?.[0];
    // Reset so choosing the same file again still triggers onChange.
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      logoError('Please select an image (PNG or JPEG).');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      logoError('Logo must be under 1 MB.');
      return;
    }

    try {
      const { width, height } = await readImageSize(file);
      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        logoError(
          `Logo must be no larger than ${MAX_WIDTH} × ${MAX_HEIGHT} pixels.`
        );
        return;
      }
    } catch {
      logoError('That image could not be read.');
      return;
    }

    setIsLogoUploading(true);
    try {
      const token = await user.getIdToken();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('username', user.email);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/prices/logo`,
        formData,
        {
          headers: {
            authorization: token,
          },
        }
      );

      dispatch(
        updateGeneralPayment({
          newValue: response.data.key,
          fieldToUpdate: 'companyLogoKey',
        })
      );
      // A new logo resets the accent to follow it (saved by the server).
      dispatch(
        updateGeneralPayment({
          newValue: response.data.logoColor || '',
          fieldToUpdate: 'companyLogoColor',
        })
      );
      dispatch(
        updateGeneralPayment({ newValue: '', fieldToUpdate: 'companyColor' })
      );
      dispatch(updateLogoUrl(response.data.url));
      setLogoPreview(response.data.url);
      toast({
        title: 'Logo uploaded',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
      logoError(
        error.response?.data?.message || 'Please try again in a moment.'
      );
    }
    setIsLogoUploading(false);
  };

  // Swatch shows what the PDF will use: the custom color, else the color
  // detected from the logo, else the default navy.
  const hexPattern = /^#[0-9a-f]{6}$/i;
  const accentSwatch = hexPattern.test(payment.general.companyColor || '')
    ? payment.general.companyColor
    : hexPattern.test(payment.general.companyLogoColor || '')
    ? payment.general.companyLogoColor
    : '#1f3a5f';

  // Saved right away (not with the form's Save) so it can't be lost.
  const handleUseLogoColor = async () => {
    try {
      const token = await user.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/prices/company-color`,
        { username: user.email, companyColor: '' },
        { headers: { authorization: token } }
      );
      dispatch(
        updateGeneralPayment({ newValue: '', fieldToUpdate: 'companyColor' })
      );
    } catch (error) {
      console.error(error);
      toast({
        title: 'Could not switch to the logo color',
        description: 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const openHelpModal = () => {
    console.log('help modal');
    setOpenModal(true);
  };

  const handleSave = async () => {
    try {
      // if (process.env.REACT_APP_ENVIRONMENT === 'prod') {
      //   mixpanel.track('User saved the payment labor section.');
      // }
      let tab;
      if (currentTab === 0) {
        tab = 'options';
      } else if (currentTab === 1) {
        tab = 'general';
      } else if (currentTab === 2) {
        tab = 'commission';
      }

      const token = await user.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/labors/payment`,
        {
          username: user.email,
          payment,
          tab,
        },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      dispatch(updateHasBeenSaved(tab));
      setUnsavedChanges(false);
      checkPaymentCompletion(payment, [...savedList, tab]);
    } catch (error) {
      console.log('error: ' + error);
    }

    toast({
      title: 'Changes saved',
      description: 'Your pricing was updated.',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleTabChange = index => {
    setCurrentTab(index);
  };

  return (
    <Box>
      <Tabs
        align="center"
        variant="soft-rounded"
        colorScheme="blue"
        onChange={handleTabChange}
        index={currentTab}
      >
        <TabList>
          <Tab
            as="b"
            bg={isPaymentOptionsComplete ? 'green.200' : 'yellow.200'}
            color="black"
            _selected={{
              border: '2px solid black',
              bg: isPaymentOptionsComplete ? 'green.400' : 'yellow.400',
            }}
          >
            Options
          </Tab>
          <Tab
            as="b"
            bg={isPaymentGeneralComplete ? 'green.200' : 'yellow.200'}
            color="black"
            _selected={{
              border: '2px solid black',
              bg: isPaymentGeneralComplete ? 'green.400' : 'yellow.400',
            }}
          >
            General
          </Tab>
          <Tab
            as="b"
            bg={isPaymentCommissionComplete ? 'green.200' : 'yellow.200'}
            color="black"
            _selected={{
              border: '2px solid black',
              bg: isPaymentCommissionComplete ? 'green.400' : 'yellow.400',
            }}
          >
            Commission
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Option</Th>
                  <Th>Fee</Th>
                  <Th>Years</Th>
                  <Th>Interest Rate</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>
                    <Input
                      size="sm"
                      maxWidth="450px"
                      value={payment.options.oneYearDescription}
                      onChange={event => {
                        setUnsavedChanges(true);
                        dispatch(
                          updatePaymentOptions({
                            newValue: event.target.value,
                            fieldToUpdate: 'oneYearDescription',
                          })
                        );
                      }}
                    ></Input>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="70px"
                        value={payment.options.oneYearFee ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updatePaymentOptions({
                              newValue: value,
                              fieldToUpdate: 'oneYearFee',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                  <Td>
                    <NumberInput
                      size="sm"
                      maxWidth="70px"
                      value={payment.options.oneYearYears ?? ''}
                      onChange={value => {
                        setUnsavedChanges(true);
                        dispatch(
                          updatePaymentOptions({
                            newValue: value,
                            fieldToUpdate: 'oneYearYears',
                          })
                        );
                      }}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="70px"
                        value={payment.options.oneYearRate ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updatePaymentOptions({
                              newValue: value,
                              fieldToUpdate: 'oneYearRate',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    {/* <Text>3 year 3.5%</Text> */}
                    <Input
                      size="sm"
                      maxWidth="450px"
                      value={payment.options.threeYearDescription}
                      onChange={event => {
                        setUnsavedChanges(true);
                        dispatch(
                          updatePaymentOptions({
                            newValue: event.target.value,
                            fieldToUpdate: 'threeYearDescription',
                          })
                        );
                      }}
                    ></Input>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="70px"
                        value={payment.options.threeYearFee ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updatePaymentOptions({
                              newValue: value,
                              fieldToUpdate: 'threeYearFee',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                  <Td>
                    {/* <Text>3</Text> */}
                    <NumberInput
                      size="sm"
                      maxWidth="70px"
                      value={payment.options.threeYearYears ?? ''}
                      onChange={value => {
                        setUnsavedChanges(true);
                        dispatch(
                          updatePaymentOptions({
                            newValue: value,
                            fieldToUpdate: 'threeYearYears',
                          })
                        );
                      }}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="70px"
                        value={payment.options.threeYearRate ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updatePaymentOptions({
                              newValue: value,
                              fieldToUpdate: 'threeYearRate',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    {/* <Text>7 year 6.9%</Text> */}
                    <Input
                      size="sm"
                      maxWidth="450px"
                      value={payment.options.sevenYearDescription}
                      onChange={event => {
                        setUnsavedChanges(true);
                        dispatch(
                          updatePaymentOptions({
                            newValue: event.target.value,
                            fieldToUpdate: 'sevenYearDescription',
                          })
                        );
                      }}
                    ></Input>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="70px"
                        value={payment.options.sevenYearFee ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updatePaymentOptions({
                              newValue: value,
                              fieldToUpdate: 'sevenYearFee',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                  <Td>
                    {/* <Text>7</Text> */}
                    <NumberInput
                      size="sm"
                      maxWidth="70px"
                      value={payment.options.sevenYearYears ?? ''}
                      onChange={value => {
                        setUnsavedChanges(true);
                        dispatch(
                          updatePaymentOptions({
                            newValue: value,
                            fieldToUpdate: 'sevenYearYears',
                          })
                        );
                      }}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="70px"
                        value={payment.options.sevenYearRate ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updatePaymentOptions({
                              newValue: value,
                              fieldToUpdate: 'sevenYearRate',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    {/* <Text>Cash</Text> */}
                    <Input
                      size="sm"
                      maxWidth="450px"
                      value={payment.options.cashDescription}
                      onChange={event => {
                        setUnsavedChanges(true);
                        dispatch(
                          updatePaymentOptions({
                            newValue: event.target.value,
                            fieldToUpdate: 'cashDescription',
                          })
                        );
                      }}
                    ></Input>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="70px"
                        value={payment.options.cashFee ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updatePaymentOptions({
                              newValue: value,
                              fieldToUpdate: 'cashFee',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                  <Td>
                    {/* <Text>0</Text> */}
                    <NumberInput
                      size="sm"
                      maxWidth="70px"
                      value={payment.options.cashYears ?? ''}
                      onChange={value => {
                        setUnsavedChanges(true);
                        dispatch(
                          updatePaymentOptions({
                            newValue: value,
                            fieldToUpdate: 'cashYears',
                          })
                        );
                      }}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="70px"
                        value={payment.options.cashRate ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updatePaymentOptions({
                              newValue: value,
                              fieldToUpdate: 'cashRate',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
            <Text as="i" fontSize="xs">
              The final option is the default and is always available
            </Text>
          </TabPanel>
          <TabPanel>
            <Overhead openModal={openModal} setOpenModal={setOpenModal} />
            <Table p="10" size="sm">
              <Tbody>
                <Tr>
                  <Td>
                    <Text>User Name</Text>
                  </Td>
                  <Td>
                    <Text>{userEmail}</Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text>Company Name</Text>
                  </Td>
                  <Td>
                    <HStack>
                      <Input
                        size="sm"
                        maxWidth="450px"
                        value={payment.general.companyName}
                        onChange={event => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateGeneralPayment({
                              newValue: event.target.value,
                              fieldToUpdate: 'companyName',
                            })
                          );
                        }}
                      ></Input>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text>Company Logo</Text>
                  </Td>
                  <Td>
                    <HStack spacing={4}>
                      <Box
                        w="150px"
                        h="100px"
                        borderWidth="1px"
                        borderRadius="md"
                        overflow="hidden"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bg="gray.50"
                      >
                        {logoPreview ? (
                          <Image
                            src={logoPreview}
                            alt="Logo"
                            maxW="100%"
                            maxH="100%"
                            objectFit="contain"
                          />
                        ) : (
                          <Text color="gray.500">No logo</Text>
                        )}
                      </Box>

                      <input
                        ref={inputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={handleLogoUpload}
                        style={{ display: 'none' }}
                      />

                      <Button
                        size="sm"
                        onClick={() => inputRef.current.click()}
                        isLoading={isLogoUploading}
                      >
                        <Text fontSize="xs">
                          {logoPreview ? 'Replace' : 'Upload'}
                        </Text>
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text>Estimate Accent Color</Text>
                  </Td>
                  <Td>
                    <HStack spacing={3}>
                      <Input
                        type="color"
                        size="sm"
                        width="56px"
                        padding="2px"
                        value={accentSwatch}
                        onChange={event => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateGeneralPayment({
                              newValue: event.target.value,
                              fieldToUpdate: 'companyColor',
                            })
                          );
                        }}
                      />
                      <Text fontSize="xs" color="gray.600">
                        {payment.general.companyColor
                          ? `Custom ${payment.general.companyColor.toUpperCase()}`
                          : payment.general.companyLogoColor
                          ? `Matches your logo (${payment.general.companyLogoColor.toUpperCase()})`
                          : 'Default navy'}
                      </Text>
                      {payment.general.companyColor &&
                        payment.general.companyLogoColor && (
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={handleUseLogoColor}
                          >
                            Use logo color
                          </Button>
                        )}
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text>Address Line 1</Text>
                  </Td>
                  <Td>
                    <HStack>
                      <Input
                        size="sm"
                        maxWidth="450px"
                        value={payment.general.companyAddressLineOne}
                        onChange={event => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateGeneralPayment({
                              newValue: event.target.value,
                              fieldToUpdate: 'companyAddressLineOne',
                            })
                          );
                        }}
                      ></Input>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text>Address Line 2</Text>
                  </Td>
                  <Td>
                    <HStack>
                      <Input
                        size="sm"
                        maxWidth="450px"
                        value={payment.general.companyAddressLineTwo}
                        onChange={event => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateGeneralPayment({
                              newValue: event.target.value,
                              fieldToUpdate: 'companyAddressLineTwo',
                            })
                          );
                        }}
                      ></Input>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text>Phone Number</Text>
                  </Td>
                  <Td>
                    <HStack>
                      <Input
                        size="sm"
                        maxWidth="450px"
                        value={payment.general.companyPhoneNumber}
                        onChange={event => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateGeneralPayment({
                              newValue: event.target.value,
                              fieldToUpdate: 'companyPhoneNumber',
                            })
                          );
                        }}
                      ></Input>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text>Email</Text>
                  </Td>
                  <Td>
                    <HStack>
                      <Input
                        size="sm"
                        maxWidth="450px"
                        value={payment.general.companyEmail}
                        onChange={event => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateGeneralPayment({
                              newValue: event.target.value,
                              fieldToUpdate: 'companyEmail',
                            })
                          );
                        }}
                      ></Input>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text>Sales tax on material</Text>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="150px"
                        value={payment.general.salesTax ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateGeneralPayment({
                              newValue: value,
                              fieldToUpdate: 'salesTax',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text>
                      Percentage to Deduct From Material Cost For Labor Only
                    </Text>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="150px"
                        value={payment.general.laborDeduction ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateGeneralPayment({
                              newValue: value,
                              fieldToUpdate: 'laborDeduction',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text>
                      Amount To Add To Estimate For Unforeseen Expenses
                    </Text>
                  </Td>
                  <Td>
                    <HStack>
                      <Text as="b">$</Text>
                      <NumberInput
                        size="sm"
                        maxWidth="150px"
                        value={payment.general.unforeseenExpenses ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateGeneralPayment({
                              newValue: value,
                              fieldToUpdate: 'unforeseenExpenses',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <HStack>
                      <Text>Overhead %</Text>
                      {/* <Tooltip
                        hasArrow
                        label={
                          <Box>
                            <Text>Click for help</Text>
                          </Box>
                        }
                        bg="gray.300"
                        color="black"
                        fontSize="sm"
                      >
                        <QuestionIcon
                          onClick={() => {
                            openHelpModal();
                          }}
                        ></QuestionIcon>
                      </Tooltip> */}
                    </HStack>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="150px"
                        value={payment.general.overhead ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateGeneralPayment({
                              newValue: value,
                              fieldToUpdate: 'overhead',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <HStack>
                      {/* <QuestionIcon
                        onClick={() => {
                          openHelpModal();
                        }}
                      ></QuestionIcon> */}
                      <Text>Profit Margin %</Text>
                    </HStack>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="150px"
                        value={payment.general.profitMargin ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateGeneralPayment({
                              newValue: value,
                              fieldToUpdate: 'profitMargin',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text>Approx. income tax on sales income</Text>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="150px"
                        value={payment.general.salesIncomeTax ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateGeneralPayment({
                              newValue: value,
                              fieldToUpdate: 'salesIncomeTax',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text>Approx. income tax on company profit</Text>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="150px"
                        value={payment.general.profitIncomeTax ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateGeneralPayment({
                              newValue: value,
                              fieldToUpdate: 'profitIncomeTax',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
            <Box mt={6} textAlign="left">
              <Text fontWeight="bold" mb={3}>
                Attachment Library
              </Text>
              <HStack justify="space-between" mb={3}>
                <Text fontSize="xs" color="gray.500">
                  {!libraryItems.length ? 'No stored attachments' : ''}
                </Text>
                <input
                  ref={libraryInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleLibraryUpload}
                  style={{ display: 'none' }}
                />
                <Button
                  size="sm"
                  leftIcon={<AddIcon boxSize={2.5} />}
                  onClick={() => libraryInputRef.current.click()}
                  isLoading={libraryUploading}
                >
                  Upload to Library
                </Button>
              </HStack>

              {libraryItems.length > 0 && (
                <VStack align="stretch" spacing={2}>
                  {libraryItems.map(item => (
                    <HStack
                      key={item.id}
                      spacing={3}
                      p={2}
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      bg="white"
                      opacity={libraryBusyId === item.id ? 0.6 : 1}
                    >
                      <Box flex={1} minW={0}>
                        <Text fontSize="sm" noOfLines={1}>
                          {item.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {formatFileSize(item.size)}
                          {item.createdAt &&
                            ` · added ${new Date(
                              item.createdAt
                            ).toLocaleDateString()}`}
                        </Text>
                      </Box>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        icon={<DeleteIcon />}
                        aria-label={`Delete ${item.name} from library`}
                        onClick={() => handleLibraryDelete(item)}
                        isDisabled={libraryBusyId === item.id}
                      />
                    </HStack>
                  ))}
                </VStack>
              )}
            </Box>
          </TabPanel>
          <TabPanel>
            <Table p="10" size="sm">
              <Tbody>
                <Tr>
                  <Td rowSpan={3}>
                    <Text>Sales Commission</Text>
                  </Td>
                  <Td>
                    <Checkbox
                      isChecked={payment.commission.isFlatRate}
                      onChange={event => {
                        setUnsavedChanges(true);
                        dispatch(
                          updateCommission({
                            newValue: event.target.checked,
                            fieldToUpdate: 'isFlatRate',
                          })
                        );
                        dispatch(
                          updateCommission({
                            newValue: false,
                            fieldToUpdate: 'isPercentageOfSale',
                          })
                        );
                        dispatch(
                          updateCommission({
                            newValue: false,
                            fieldToUpdate: 'isPercentageOfProfit',
                          })
                        );
                      }}
                    />
                  </Td>
                  <Td>
                    <Text>Flat Rate</Text>
                  </Td>
                  <Td>
                    <HStack>
                      <Text as="b">$</Text>
                      <NumberInput
                        size="sm"
                        maxWidth="150px"
                        value={payment.commission.flatRate ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateCommission({
                              newValue: value,
                              fieldToUpdate: 'flatRate',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Checkbox
                      isChecked={payment.commission.isPercentageOfSale}
                      onChange={event => {
                        setUnsavedChanges(true);
                        dispatch(
                          updateCommission({
                            newValue: event.target.checked,
                            fieldToUpdate: 'isPercentageOfSale',
                          })
                        );
                        dispatch(
                          updateCommission({
                            newValue: false,
                            fieldToUpdate: 'isPercentageOfProfit',
                          })
                        );
                        dispatch(
                          updateCommission({
                            newValue: false,
                            fieldToUpdate: 'isFlatRate',
                          })
                        );
                      }}
                    />
                  </Td>
                  <Td>
                    <Text>Percentage of Sale</Text>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="150px"
                        value={payment.commission.percentageOfSale ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateCommission({
                              newValue: value,
                              fieldToUpdate: 'percentageOfSale',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Checkbox
                      isChecked={payment.commission.isPercentageOfProfit}
                      onChange={event => {
                        setUnsavedChanges(true);
                        dispatch(
                          updateCommission({
                            newValue: event.target.checked,
                            fieldToUpdate: 'isPercentageOfProfit',
                          })
                        );
                        dispatch(
                          updateCommission({
                            newValue: false,
                            fieldToUpdate: 'isFlatRate',
                          })
                        );
                        dispatch(
                          updateCommission({
                            newValue: false,
                            fieldToUpdate: 'isPercentageOfSale',
                          })
                        );
                      }}
                    />
                  </Td>
                  <Td>
                    <Text>Percentage of Net Profit</Text>
                  </Td>
                  <Td>
                    <HStack>
                      <NumberInput
                        size="sm"
                        maxWidth="150px"
                        value={payment.commission.percentageOfProfit ?? ''}
                        onChange={value => {
                          setUnsavedChanges(true);
                          dispatch(
                            updateCommission({
                              newValue: value,
                              fieldToUpdate: 'percentageOfProfit',
                            })
                          );
                        }}
                      >
                        <NumberInputField />
                      </NumberInput>
                      <Text as="b">%</Text>
                    </HStack>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </TabPanel>
        </TabPanels>
      </Tabs>
      <Center>
        <VStack>
          {unsavedChanges && (
            <Alert status="warning" marginTop="5">
              <AlertIcon />
              <AlertTitle>You have unsaved changes!</AlertTitle>
            </Alert>
          )}
          {((currentTab === 0 && !savedList.includes('options')) ||
            (currentTab === 1 && !savedList.includes('general')) ||
            (currentTab === 2 && !savedList.includes('commission'))) && (
            <Alert status="info" marginTop="5">
              <AlertIcon />
              <AlertTitle>Review and Save</AlertTitle>
            </Alert>
          )}
          <Button colorScheme="blue" marginTop="3" onClick={handleSave}>
            Save
          </Button>
        </VStack>
      </Center>
    </Box>
  );
};

export default PaymentOptions;
